import { AlertTriangle, Moon, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { useUi } from '../../../stores/ui';
import { AuthMurmuration } from './AuthMurmuration';
import styles from './AuthLayout.module.css';

/**
 * The frame every AUTH screen sits in.
 *
 * Two halves: a deep stage carrying the brand, and an access panel carrying the
 * task. Sign-in is the only screen a person meets before the shell exists, so it
 * is the product's only chance to say what it is — and giving the form its own
 * column keeps it the same width at every viewport, which is why the eye lands
 * on it before it lands on the artwork.
 *
 * Below tablet the stage folds into a banner and the form takes the page, since
 * a login form is the one screen everybody meets on whatever device is nearest.
 *
 * There is no "create an account" link anywhere in this layout, on purpose.
 * Accounts exist only via invite (S3 build notes), so the absence is the feature.
 */

interface AuthLayoutProps {
  readonly title: string;
  readonly subtitle?: string;
  /** A status tone for the screens that are reporting a state, not asking for input. */
  readonly tone?: 'default' | 'warning' | 'danger';
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** Secondary links under the form — "back to sign in" and friends. */
  readonly footer?: ReactNode;
}

export function AuthLayout({
  title,
  subtitle,
  tone = 'default',
  icon,
  children,
  footer,
}: AuthLayoutProps): ReactNode {
  return (
    <div className={styles.shell}>
      <aside className={styles.stage} aria-label="Nest Recruit">
        <AuthMurmuration />
        <div className={styles.scrim} aria-hidden />
        <svg className={styles.grain} aria-hidden focusable="false">
          <filter id="authGrain">
            <feTurbulence type="fractalNoise" baseFrequency="0.82" numOctaves="3" stitchTiles="stitch" />
          </filter>
          <rect width="100%" height="100%" filter="url(#authGrain)" />
        </svg>

        <div className={styles.brand}>
          <NestMark />
          <span className={styles.wordmark}>Nest Recruit</span>
        </div>

        <div className={styles.lockup}>
          {/* One sentence, three beats — the line break is the timing. */}
          <p className={styles.display}>
            <span className={styles.reveal} data-step="1">
              Talent
            </span>
            <span className={styles.reveal} data-step="2">
              doesn&rsquo;t wait
            </span>
            <span className={`${styles.reveal} ${styles.displayHighlight}`} data-step="3">
              Neither do we
            </span>
          </p>
        </div>
      </aside>

      <main className={styles.access}>
        <div className={`${styles.panel} ${styles.reveal}`} data-tone={tone}>
          {icon !== undefined && <div className={styles.icon}>{icon}</div>}

          <h1 className={styles.title}>{title}</h1>
          {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}

          <div className={styles.body}>{children}</div>

          {footer !== undefined && <div className={styles.footer}>{footer}</div>}
        </div>

        <div className={styles.accessFooter}>
          <ThemeToggle />
        </div>
      </main>
    </div>
  );
}

/** Three birds out of the murmuration, banking together. */
function NestMark(): ReactNode {
  return (
    <svg className={styles.mark} viewBox="0 0 34 34" fill="none" aria-hidden>
      <path
        d="M4 20 L11 26 L18 17"
        stroke="var(--flock-blue-light)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 13 L19 19 L26 10"
        stroke="var(--flock-hero-ink)"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 26 L26 29 L30 24"
        stroke="var(--flock-blue)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The shell owns the theme everywhere else, but the shell does not exist yet on
 * these screens — and someone signing in at night should not have to sign in
 * first to turn the lights down.
 */
function ThemeToggle(): ReactNode {
  const theme = useUi((state) => state.theme);
  const toggleTheme = useUi((state) => state.toggleTheme);

  return (
    <Button
      variant="ghost"
      size="sm"
      icon={theme === 'dark' ? Sun : Moon}
      onClick={toggleTheme}
    >
      {theme === 'dark' ? 'Light mode' : 'Dark mode'}
    </Button>
  );
}

/**
 * A summary error above a form — the ones that belong to no single field.
 *
 * Keyed on the message so React remounts it when the text changes, which is what
 * re-runs the shake. A second failed password otherwise re-renders the same box
 * in the same place saying nearly the same thing, and nothing on screen moves.
 */
export function AuthError({ message }: { message: string }): ReactNode {
  return (
    <p className={styles.formError} role="alert" key={message}>
      <AlertTriangle className={styles.formErrorIcon} size={15} aria-hidden />
      {message}
    </p>
  );
}

/** A confirmation that replaces a form once it has done its job. */
export function AuthNotice({
  tone = 'success',
  children,
}: {
  readonly tone?: 'success' | 'info';
  readonly children: ReactNode;
}): ReactNode {
  return (
    <p className={styles.notice} data-tone={tone}>
      {children}
    </p>
  );
}
